/**
 * Öffentliche Einwilligungs-Bestätigung (Double-Opt-in).
 * Kunde öffnet /einwilligung/:token und bestätigt die Marketing-Einwilligung.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import DealerBrandLogo from "../components/brand/DealerBrandLogo";
import axios from "axios";
import { ShieldCheck, CheckCircle2, Loader2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNELS = [
    { k: "email", label: "E-Mail" },
    { k: "sms", label: "SMS" },
    { k: "whatsapp", label: "WhatsApp" },
];

export default function PublicConsentPage() {
    const { token } = useParams();
    const [info, setInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [channels, setChannels] = useState(["email"]);
    const [saving, setSaving] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/public/consent/${token}`);
                setInfo(data);
                if (data.channels?.length) setChannels(data.channels);
                if (data.status === "confirmed") setDone(true);
            } catch (e) {
                setError(e?.response?.data?.detail || "Link ungültig oder abgelaufen.");
            } finally { setLoading(false); }
        })();
    }, [token]);

    const toggle = (k) => setChannels((c) => c.includes(k) ? c.filter((x) => x !== k) : [...c, k]);

    const confirm = async () => {
        setSaving(true);
        try {
            await axios.post(`${API}/public/consent/${token}`, { channels });
            setDone(true);
        } catch (e) {
            setError(e?.response?.data?.detail || "Bestätigung fehlgeschlagen.");
        } finally { setSaving(false); }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="public-consent-page">
            <div className="bg-white border border-gray-200 rounded-sm shadow-sm w-full max-w-md p-7">
                {info && (info.dealer_logo || info.dealer) && (
                    <div className="mb-4 pb-4 border-b border-gray-100" data-testid="consent-dealer-logo">
                        <DealerBrandLogo variant="light" logoUrl={info.dealer_logo} companyName={info.dealer}
                            imgClassName="h-9 max-w-[190px]" textClassName="text-lg" />
                    </div>
                )}
                <div className="flex items-center gap-2 mb-4">
                    <ShieldCheck className="w-6 h-6 text-emerald-600" />
                    <h1 className="font-bold text-lg">Einwilligung</h1>
                </div>

                {loading ? (
                    <div className="flex items-center gap-2 text-gray-500 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Lädt…</div>
                ) : error ? (
                    <div className="flex items-center gap-2 text-red-600 text-sm" data-testid="public-consent-error"><XCircle className="w-5 h-5" /> {error}</div>
                ) : done ? (
                    <div className="text-center py-6" data-testid="public-consent-done">
                        <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                        <p className="font-bold">Vielen Dank!</p>
                        <p className="text-sm text-gray-500 mt-1">Ihre Einwilligung wurde gespeichert. Sie können diese jederzeit widerrufen.</p>
                    </div>
                ) : (
                    <>
                        <p className="text-sm text-gray-600 mb-4">
                            Hallo <b>{info?.customer_name}</b>, darf <b>{info?.dealer}</b> Sie über Service-Aktionen,
                            Termine und Gutscheine informieren? Bitte wählen Sie die gewünschten Kanäle:
                        </p>
                        <div className="flex flex-wrap gap-2 mb-5">
                            {CHANNELS.map((o) => (
                                <button key={o.k} type="button" onClick={() => toggle(o.k)}
                                    className={`text-sm px-4 py-2 rounded-sm border font-medium ${channels.includes(o.k) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-gray-600 border-gray-300"}`}
                                    data-testid={`public-consent-channel-${o.k}`}>
                                    {o.label}
                                </button>
                            ))}
                        </div>
                        <button onClick={confirm} disabled={saving || !channels.length}
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-sm py-2.5 flex items-center justify-center gap-2 disabled:opacity-50"
                            data-testid="public-consent-confirm">
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Einwilligung bestätigen
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}
